/**
 * Док черновика (§6.1) — четвёртый элемент хрома приложения.
 *
 * Док не плавает над таблицей: он живёт в слоте оболочки (`AppShell dock`),
 * поэтому тело экрана сжимается и ничего не перекрывается. При нуле правок
 * дока нет вообще — ни полосы, ни пустого места. Наблюдатель (без прав на
 * правку) не видит дока никогда (§11.4).
 *
 * Левая зона — сводка и состояние записи, центр — переключатель «только мои
 * правки», справа — «Отменить всё» через модалку и главная кнопка.
 * Главная кнопка ведёт на ревью (§6.3): там видно, что именно уйдёт в чат,
 * и там же решаются конфликты. Публикация живёт ровно в одном месте.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import type { ChannelCode } from '@shared/types.ts'
import { Button, IconButton, IconClose, Modal, Tooltip } from '../../components/index.js'
import { draftSummary } from '../../app/AppShell.js'
import { useApp } from '../../store/app-store.js'
import { texts } from '../../texts.js'
import { DraftReviewScreen } from './DraftReviewScreen.js'
import { draftTexts, formatDay } from './texts.draft.js'
import './DraftDock.css'

/** «сохранено» живёт 1,5 с после каждой записи (§6.1). */
const SAVED_FOR = 1500

export interface DraftDockProps {
  /**
   * Панель массовых действий (§5.7): при выделении строк она занимает левую
   * зону дока, а главная кнопка остаётся на месте. Наполняет её волна таблицы.
   */
  selectionPanel?: ReactNode
}

export function DraftDock({ selectionPanel }: DraftDockProps) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [params] = useSearchParams()

  const changes = useApp((s) => s.draftChanges)
  const undoLast = useApp((s) => s.undoLast)
  const permissions = useApp((s) => s.bootstrap?.user.permissions) ?? []
  const defaultChannel = useApp((s) => s.bootstrap?.settings.defaultChannel) ?? 'retail'

  const count = changes.length
  const brandsCount = useMemo(
    () => new Set(changes.map((change) => change.brandId).filter((id): id is number => id !== null)).size,
    [changes],
  )

  // «сохранено» показывается после каждой записи в черновик, но не при загрузке.
  const [saved, setSaved] = useState(false)
  const seen = useRef(false)
  useEffect(() => {
    if (!seen.current) { seen.current = true; return }
    setSaved(true)
    const timer = setTimeout(() => setSaved(false), SAVED_FOR)
    return () => clearTimeout(timer)
  }, [changes])

  const [discarding, setDiscarding] = useState(false)
  /* Ревью открывается окном поверх экрана, а не отдельной страницей: после
     публикации человек остаётся там же, где был, — в таблице на том же месте
     или в карточке бренда на той же вкладке. Раньше его выбрасывало на
     начальный экран. */
  const [reviewing, setReviewing] = useState(false)

  const mayEdit = permissions.includes('brands.edit') || permissions.includes('brands.create')
  const onReview = pathname === '/draft'
  const visible = mayEdit && count > 0 && !onReview

  // ⌘↵ / Ctrl+↵ из любого места открывает ревью (§6.1). Внутри самого ревью
  // эту комбинацию слушает его форма — она публикует пакет.
  useEffect(() => {
    if (!visible) return
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Enter' || !(event.metaKey || event.ctrlKey)) return
      event.preventDefault()
      setReviewing(true)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [visible])

  if (!visible) return null

  const rawChannel = params.get('ch')
  const channel: ChannelCode = rawChannel === 'retail' || rawChannel === 'b2b' ? rawChannel : defaultChannel

  /* Фильтр «только мои правки» живёт в адресе, поэтому и выключается им же. */
  const onlyMine = params.get('mine') === '1'
  const backToAll = (() => {
    const next = new URLSearchParams(params)
    next.delete('mine')
    const query = next.toString()
    return query === '' ? '/brands' : `/brands?${query}`
  })()

  return (
    <div className="dock" role="region" aria-label={draftTexts.dock.region}>
      {selectionPanel ?? (
        <div className="dock__summary">
          <span className="dock__dot" aria-hidden="true" />
          <span className="dock__text t-cell">
            {`${draftTexts.dock.label} · ${draftSummary(count, brandsCount)}`}
          </span>
          <span className="dock__state t-meta" aria-live="polite">
            {saved ? draftTexts.dock.saved : ''}
          </span>
        </div>
      )}

      {/* При выделении строк левая зона отдана массовым действиям — фильтр уходит.
          Ссылка переключает фильтр в обе стороны: раньше она только включала
          «мои правки», и вернуться ко всем брендам было нечем. */}
      {selectionPanel ? null : (
        <Link
          className="dock__mine t-meta"
          to={onlyMine ? backToAll : `/brands?ch=${channel}&mine=1`}
        >
          {onlyMine ? texts.action.draftShowAll : texts.action.draftShowMine}
        </Link>
      )}

      <div className="dock__actions">
        {/* Шаг назад. У Cmd+Z обязан быть видимый эквивалент: иначе о нём
            знает только тот, кто его написал. Массовая простановка снимается
            одной кнопкой целиком — она и записывалась одной операцией. */}
        <Tooltip content={draftTexts.dock.undoHint}>
          <Button variant="ghost" size="m" onClick={() => void undoLast()}>
            {draftTexts.dock.undoLast}
          </Button>
        </Tooltip>

        <Button variant="danger-ghost" size="m" onClick={() => setDiscarding(true)}>
          {draftTexts.dock.cancelAll}
        </Button>

        {/* Подписи горячей клавиши на кнопке больше нет: ⌘↵ работает, но
            печатать её на самой заметной кнопке незачем. */}
        <Tooltip content={texts.action.commitHint}>
          <Button
            className="dock__commit"
            variant="primary"
            size="m"
            aria-label={draftTexts.dock.commitAria(count)}
            onClick={() => setReviewing(true)}
          >
            <span className="dock__commit-text">
              {draftTexts.dock.commitText}
              <span className="dock__commit-count" key={count}>{` (${count})`}</span>
            </span>
          </Button>
        </Tooltip>
      </div>

      <DiscardDraftModal
        open={discarding}
        count={count}
        onClose={() => setDiscarding(false)}
        onReview={() => { setDiscarding(false); setReviewing(true) }}
      />

      <Modal
        open={reviewing}
        onClose={() => setReviewing(false)}
        title={texts.screen.draft.title}
        size="l"
      >
        <DraftReviewScreen embedded onClose={() => setReviewing(false)} />
      </Modal>
    </div>
  )
}

// ─────────────────── плашка возврата к черновику (§6.6) ──────────────────────

/**
 * §6.6: `beforeunload` не используется — черновик на сервере, пугать нечем.
 * Вместо этого при возврате под строкой управления появляется одна строка
 * 36 px с честным числом и четырьмя выходами. Показывается раз за сессию.
 *
 * Компонент самодостаточный: его место — слот `banners` оболочки, он заменяет
 * собой текущий блок `banner--draft` в `app/AppBanners.tsx` (см. отчёт).
 */
const RETURN_BANNER_KEY = 'bm.draftReturnBanner'

export function DraftReturnBanner() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const changes = useApp((s) => s.draftChanges)
  const permissions = useApp((s) => s.bootstrap?.user.permissions) ?? []
  const defaultChannel = useApp((s) => s.bootstrap?.settings.defaultChannel) ?? 'retail'

  const [hidden, setHidden] = useState(() => {
    try { return sessionStorage.getItem(RETURN_BANNER_KEY) === '1' } catch { return false }
  })
  const [discarding, setDiscarding] = useState(false)

  function hide() {
    setHidden(true)
    try { sessionStorage.setItem(RETURN_BANNER_KEY, '1') } catch { /* приватный режим */ }
  }

  const mayEdit = permissions.includes('brands.edit') || permissions.includes('brands.create')
  const count = changes.length
  if (hidden || !mayEdit || count === 0) return null

  const startedAt = changes.reduce<string | null>(
    (first, change) => (first === null || change.createdAt < first ? change.createdAt : first),
    null,
  )
  const rawChannel = params.get('ch')
  const channel: ChannelCode = rawChannel === 'retail' || rawChannel === 'b2b' ? rawChannel : defaultChannel

  return (
    <div className="draft-return" role="status" aria-label={draftTexts.returnBanner.region}>
      <span className="draft-return__text t-cell">
        {draftTexts.returnBanner.text(count, formatDay(startedAt))}
      </span>
      <Link className="draft-return__link t-cell" to={`/brands?ch=${channel}&mine=1`}>
        {draftTexts.returnBanner.showOnlyMine}
      </Link>
      <Link className="draft-return__link t-cell" to="/draft">
        {texts.action.openReview}
      </Link>
      <button
        type="button"
        className="draft-return__link draft-return__link--danger t-cell"
        onClick={() => setDiscarding(true)}
      >
        {texts.draftPopover.discard}
      </button>
      <IconButton
        className="draft-return__close"
        label={draftTexts.returnBanner.close}
        icon={<IconClose />}
        size="s"
        onClick={hide}
      />

      <DiscardDraftModal
        open={discarding}
        count={count}
        onClose={() => setDiscarding(false)}
        onReview={() => { setDiscarding(false); navigate('/draft') }}
      />
    </div>
  )
}

// ───────────────────────── отмена всего черновика (§6.1) ─────────────────────

export interface DiscardDraftModalProps {
  open: boolean
  count: number
  onClose: () => void
  /**
   * Главная кнопка модалки — «Сначала посмотреть ревью» (§6.3). На самом ревью
   * смотреть уже некуда, и экран передаёт сюда только закрытие.
   */
  onReview?: () => void
}

/**
 * Необратимое действие: окно не закрывается ни Esc, ни кликом по подложке —
 * выход только явной кнопкой (§2.3, §6.3).
 */
export function DiscardDraftModal({ open, count, onClose, onReview }: DiscardDraftModalProps) {
  const discardDraft = useApp((s) => s.discardDraft)
  const [busy, setBusy] = useState(false)

  async function discard() {
    setBusy(true)
    await discardDraft()
    setBusy(false)
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissible={false}
      size="s"
      title={draftTexts.discard.title(count)}
      footerNote={draftTexts.discard.note}
      footer={(
        <>
          <Button variant="danger-ghost" loading={busy} onClick={() => void discard()}>
            {draftTexts.discard.confirm}
          </Button>
          <Button variant="secondary" onClick={onReview ?? onClose}>
            {onReview ? draftTexts.discard.reviewFirst : draftTexts.discard.keep}
          </Button>
        </>
      )}
    >
      {draftTexts.discard.body}
    </Modal>
  )
}
